import { useEffect, useState, forwardRef, InputHTMLAttributes } from "react";
import { Modal } from "react-bootstrap";
import { APISchemaRelease, APISchemaChange, ChangelogChangeType } from '../../models/api';
import { Controller, useForm, useFieldArray } from "react-hook-form";
import { FormErrorMessage } from "..";
import DatePicker, { ReactDatePickerCustomHeaderProps } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

type Props = {
  show: boolean;
  // editType: number;
  handleShow: (value: boolean) => void;
  onAddRelease: (form: APISchemaRelease) => void;
  onUpdateRelease: (id: string, form: APISchemaRelease) => void;
  onDeleteRelease: (id: string) => void;
  release: APISchemaRelease | null;
  setRelease: (value: APISchemaRelease | null) => void;
};

export function ModalUpdateRelease(props: Props): JSX.Element {
  const { show, handleShow, onAddRelease, onUpdateRelease, onDeleteRelease, release, setRelease } = props;

  const { register, control, handleSubmit, formState: { errors }, reset } = useForm<APISchemaRelease>({
    mode: "onBlur",
    defaultValues: {
      version: "",
      releaseDate: (new Date()).toISOString(),
      changes: [{ type: 0, description: "" }]
    },
    criteriaMode: "all",
  });

  useEffect(() => {
    if (release) {

      const updatedRelease = {
        ...release,
        changes: release.changes.map((change) => ({
          ...change,
          releaseId: release.id,
        })),
      };
      // console.log("the release that populated the form is:", updatedRelease);

      reset(updatedRelease);
    } else {
      reset({
        version: "",
        releaseDate: new Date().toISOString(),
        changes: [{ type: 0, description: "" }],
      });
    }
  }, [release, reset]);


  const { fields, append, remove } = useFieldArray({
    control,
    name: "changes"
  });

  const modalHideHandler = () => {
    setRelease(null);
    handleShow(false);
    reset();
  };

  function isValidVersionFormat(input: string) {
    const regex = /^\d+\.\d+\.\d+$/;
    return regex.test(input) || "Version must be in proper numeric format, example: 1.2.3";
  }

  const onSubmitHandler = async (form: APISchemaRelease) => {
    // console.log("the form input is:", form);
    if (release) {
      await onUpdateRelease(release.id, form);
      modalHideHandler();
    }
    else {
      await onAddRelease(form);
      modalHideHandler();

    }
  };

  type CustomInputProps = {
    value?: string;
    onClick?: () => void;
  } & InputHTMLAttributes<HTMLInputElement>;

  const CustomInput = forwardRef<HTMLInputElement, CustomInputProps>(
    ({ value, onClick, ...rest }, ref) => (
      <input
        className="form-control"
        onClick={onClick}
        ref={ref}
        value={value}
        readOnly
        {...rest}
      />
    )
  );


  return (
    <Modal show={show} onHide={modalHideHandler} centered id="addChangelogModal">
      <Modal.Header closeButton>
        <Modal.Title>{release ? "Update Release" : "Create New Release"}</Modal.Title>
      </Modal.Header>

      <form onSubmit={handleSubmit(onSubmitHandler)}>
        <Modal.Body>
          <div className="row">
            <div className="col-12">
              <div className="form-group">
                <label htmlFor="" className="position-relative required">
                  Version
                </label>
                <input
                  type="text"
                  {...register('version', {
                    required: "Version is required",
                    validate: isValidVersionFormat
                  })}
                  id="version"
                  className="form-control"
                  placeholder="1.2.3"
                />
                {errors.version && <FormErrorMessage message={errors.version.message || ""} />}
              </div>
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-12">
              {/* <Controller
                name="releaseDate"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    {...field}
                    selected={field.value ? new Date(field.value) : null}
                    onChange={(date) => {
                      field.onChange(date ? date.toISOString() : new Date().toISOString());
                    }}
                    dateFormat="MM/dd/yyyy"
                    placeholderText="Release Date"
                    className="form-control"
                    showYearDropdown
                    showMonthDropdown
                    dropdownMode="select"
                    customInput={<input />}
                  />
                )}
              /> */}

              <Controller
                name="releaseDate"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    {...field}
                    selected={field.value ? new Date(field.value) : null}
                    onChange={(date) => {
                      field.onChange(date ? date.toISOString() : null);
                    }}
                    dateFormat="MMM dd, yyyy" 
                    placeholderText="Release Date"
                    className="form-control"
                    showYearDropdown
                    showMonthDropdown
                    dropdownMode="select"
                    customInput={<CustomInput />}
                  />
                )}
              />

            </div>
          </div>

          <div className="row">
            <div className="col-12">
              {fields.map((field, index) => (
                <div key={field.id} className="border p-2 mb-1 rounded" style={{ backgroundColor: "rgba(230, 230, 230, 0.5)" }}>
                  <div className="d-flex flex-row align-items-start">
                    <div className="flex-grow-1">
                      <div className="form-group mb-1">
                        <select
                          className="form-control"
                          {...register(`changes.${index}.type` as const, { required: true })}
                        >
                          <option value={ChangelogChangeType.bugfix}>Bugfix</option>
                          <option value={ChangelogChangeType.feature}>Feature</option>
                          <option value={ChangelogChangeType.improvement}>Improvement</option>
                        </select>
                      </div>

                      <div className="form-group mb-1">
                        <input
                          className="form-control"
                          placeholder="Description"
                          {...register(`changes.${index}.description` as const, { required: "Description is required" })}
                        />
                        {errors.changes?.[index]?.description && (
                          <FormErrorMessage message={errors.changes[index].description?.message || ""} />
                        )}
                      </div>

                    </div>
                    {index !== 0 ? (
                      <button
                        type="button"
                        className="btn btn-sm btn-danger ms-1 mt-1"
                        style={{ padding: "0px 8px", height: "22px" }}
                        onClick={() => remove(index)}
                      >
                        X
                      </button>
                    ) :
                      (
                        <button
                          type="button"
                          className="btn btn-sm btn-danger ms-1 mt-1"
                          disabled
                          style={{ padding: "0px 8px", height: "22px" }}
                        >
                          X
                        </button>
                      )}

                  </div>

                </div>
              ))}

              <div className="text-start">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => {release ? append({ type: 0, description: "", releaseId: release.id }) :  append({ type: 0, description: "" })} }
                >
                  + Add Change
                </button>
              </div>
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <div className={`d-flex flex-row w-100  ${release ? "justify-content-between" : "justify-content-end"}`}>
            {release ? (
              <button
                type="button"
                className="btn btn-danger text-uppercase release-from-delete-btn"
                onClick={async () => {
                  await onDeleteRelease(release.id);
                  modalHideHandler();
                }}
              >
                Delete Release
              </button>
            ) : (<></>)
            }
            <div>
              <button
                type="button"
                className="btn btn-link text-site text-uppercase"
                onClick={modalHideHandler}
              >
                Cancel
              </button>

              <button
                type="submit"
                className="btn btn-primary text-uppercase release-from-submit-btn"
              >
                {release ? "Update Release" : "Create New Release"}
              </button>
            </div>
          </div>
        </Modal.Footer>
      </form>
    </Modal>
  );
}
